<template>
  <BaseModal 
    show
    size="md" 
    :title="promotion ? 'Editar Promoción' : 'Nueva Promoción'"
    :show-actions="true"
    :is-editing="!!promotion"
    :loading="isSubmitting"
    @close="$emit('close')"
    @save="handleSubmit"
  >
    <form
      class="space-y-6"
      @submit.prevent="handleSubmit"
    >
      <!-- Basic Information -->
      <PromotionBasicInfo
        :form-data="formData"
        @update:form-data="formData = $event"
      />

      <!-- Action Configuration based on type -->
      <PromotionActionConfig
        :action-type="formData.action.type"
        :form-data="formData"
        :menu-items="menuItems"
        @update:form-data="formData = $event"
      />

      <!-- Product Conditions -->
      <PromotionProductConditions
        :action-type="formData.action.type"
        :form-data="formData"
        :menu-items="menuItems"
        @update:form-data="formData = $event"
      />

      <!-- Time Conditions -->
      <PromotionTimeConditions
        :form-data="formData"
        @update:form-data="formData = $event"
      />

      <!-- Additional Configuration -->
      <PromotionAdditionalConfig
        :form-data="formData"
        @update:form-data="formData = $event"
      />
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';
import type { Promotion } from '@/types/promotion';
import type { MenuItem } from '@/services/orderService';
import BaseModal from '@/components/common/BaseModal.vue';
import PromotionBasicInfo from './PromotionBasicInfo.vue';
import PromotionActionConfig from './PromotionActionConfig.vue';
import PromotionProductConditions from './PromotionProductConditions.vue';
import PromotionTimeConditions from './PromotionTimeConditions.vue';
import PromotionAdditionalConfig from './PromotionAdditionalConfig.vue';

interface Props {
  promotion?: Promotion | null;
  menuItems: MenuItem[];
}

interface Emits {
  (e: 'close'): void;
  (e: 'save', data: Omit<Promotion, 'id' | 'created_at' | 'updated_at' | 'used_count'>): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isSubmitting = ref(false);
const hasTimeCondition = ref(false);
const promotionType = ref<'same_product' | 'different_product'>('same_product');

const formData = reactive({
  name: '',
  description: '',
  is_active: true,
  priority: 1,
  is_stackable: false,
  max_uses_per_order: undefined as number | undefined,
  max_uses_total: undefined as number | undefined,
  min_order_total: undefined as number | undefined,
  action: {
    type: '',
    value: 0,
    free_item_id: undefined as number | undefined,
    max_discount_amount: undefined as number | undefined,
    apply_to: undefined as 'cheapest' | 'most_expensive' | 'all' | undefined,
    required_quantity: undefined as number | undefined,
    target_item_id: undefined as number | undefined
  },
  time_condition: {
    days_of_week: [] as string[],
    start_time: '',
    end_time: ''
  },
  product_condition: {
    menu_item_ids: [] as number[],
    category_ids: [] as number[],
    min_quantity: undefined as number | undefined,
    max_quantity: undefined as number | undefined
  }
});

const resetForm = () => {
  Object.assign(formData, {
    name: '',
    description: '',
    is_active: true,
    priority: 1,
    is_stackable: false,
    max_uses_per_order: undefined,
    max_uses_total: undefined,
    min_order_total: undefined,
    action: {
      type: '',
      value: 0,
      free_item_id: undefined,
      max_discount_amount: undefined,
      apply_to: undefined,
      required_quantity: undefined,
      target_item_id: undefined
    },
    time_condition: {
      days_of_week: [],
      start_time: '',
      end_time: ''
    },
    product_condition: {
      menu_item_ids: [],
      category_ids: [],
      min_quantity: undefined,
      max_quantity: undefined
    }
  });
  hasTimeCondition.value = false;
  promotionType.value = 'same_product';
};

// Initialize form data if editing
watch(() => props.promotion, (promotion) => {
  if (promotion) {
    Object.assign(formData, {
      name: promotion.name,
      description: promotion.description,
      is_active: promotion.is_active,
      priority: promotion.priority,
      is_stackable: promotion.is_stackable,
      max_uses_per_order: promotion.max_uses_per_order,
      max_uses_total: promotion.max_uses_total,
      min_order_total: promotion.min_order_total,
      action: { ...formData.action, ...promotion.action },
      time_condition: promotion.time_condition || { days_of_week: [], start_time: '', end_time: '' },
      product_condition: promotion.product_condition || { menu_item_ids: [], category_ids: [], min_quantity: undefined, max_quantity: undefined }
    });
    
    hasTimeCondition.value = !!(promotion.time_condition && 
      (promotion.time_condition.days_of_week?.length || promotion.time_condition.start_time || promotion.time_condition.end_time));
  } else {
    resetForm();
  }
}, { immediate: true });

// Watch for promotion type changes to configure action values
watch(promotionType, (newType) => {
  if (newType === 'same_product') {
    // Para 2x1 del mismo producto: value = 0, free_item_id = null
    formData.action.value = 0;
    formData.action.free_item_id = undefined;
  } else {
    // Para producto diferente: mantener valores existentes o establecer defaults
    if (formData.action.value === 0) {
      formData.action.value = 1; // valor por defecto para buy_x_get_y
    }
  }
});

watch(hasTimeCondition, (value) => {
  if (!value) {
    formData.time_condition = { days_of_week: [], start_time: '', end_time: '' };
  }
});

const handleSubmit = async () => {
  isSubmitting.value = true;
  
  try {
    // Clean up the data before submitting
    const submitData: any = { ...formData };
    
    // Remove empty time condition if not enabled
    if (!hasTimeCondition.value) {
      submitData.time_condition = undefined;
    }
    
    // Remove empty product condition if no items selected
    if (!submitData.product_condition.menu_item_ids.length && !submitData.product_condition.category_ids.length) {
      submitData.product_condition = undefined;
    } else {
      // Para promociones buy_x_get_y, asegurar que min_quantity esté configurado
      if (submitData.action.type === 'buy_x_get_y' && !submitData.product_condition.min_quantity) {
        submitData.product_condition.min_quantity = 2; // Default para 2x1
      }
    }
    
    // Convert days_of_week to proper WeekDay type
    if (submitData.time_condition?.days_of_week) {
      submitData.time_condition.days_of_week = submitData.time_condition.days_of_week as any;
    }
    
    emit('save', submitData);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
